import * as React from 'react';
import { useEffect, useState, useRef } from 'react';
import FieldHandler from '../../../../models/interfaces/FieldHandler';
import OnPremObjectRequestService from '../../../../services/OnPremObjectRequestService';
import OnPremPortOpeningObject from '../../../../models/entities/OnPremPortOpeningObject';
import ReadOnlyField from './../ReadOnly';
import { LoadingIndicator, Typeahead, TypeaheadItem } from '@emorg-prd/standard-react';
import { useValidation } from '../ValidationContext';
import { TextPassage } from '../../components/TextPassage';
import { TextLink } from '../../components/TextLinkNew';

interface ObjectLookupProps extends FieldHandler<OnPremPortOpeningObject> {
	label: string;
	placeholder?: string;
	note?: string | JSX.Element;
	noteLink?: string;
}

interface ObjectLookupState {
	results: OnPremPortOpeningObject[];
	inputText: string | null;
	loading: boolean;
	allObjects: OnPremPortOpeningObject[];
	objectsLoaded: boolean;
}

let objectsCache: OnPremPortOpeningObject[] | null = null;
let objectsCachePromise: Promise<OnPremPortOpeningObject[]> | null = null;

const ObjectLookup: React.FC<ObjectLookupProps> = (props) => {
	const searchTimeout = useRef<NodeJS.Timeout | null>(null);
	const _isMounted = useRef(false);
	const objectService = new OnPremObjectRequestService();
	const [results, setResults] = useState<OnPremPortOpeningObject[]>([]);
	const [inputText, setInputText] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [allObjects, setAllObjects] = useState<OnPremPortOpeningObject[]>([]);
	const [objectsLoaded, setObjectsLoaded] = useState<boolean>(false);
	const { isValid, nonValidMessage } = useValidation(props.value, props.validation);
	const prevValueRef = useRef(props.value);

	useEffect(() => {
		_isMounted.current = true;
		resetInputText(props.value);
		loadAllObjects();
	}, []);

	useEffect(() => {
		return () => {
			_isMounted.current = false;
			if (searchTimeout.current) {
				clearTimeout(searchTimeout.current);
			}
		};
	}, []);

	useEffect(() => {
		if (prevValueRef.current !== props.value) {
			resetInputText(props.value);
			prevValueRef.current = props.value;
		}
	}, [props.value]);

	const loadAllObjects = async () => {
		try {
			if (objectsCache) {
				if (_isMounted.current) {
					setAllObjects(objectsCache);
					setObjectsLoaded(true);
				}
				return;
			}

			if (objectsCachePromise) {
				const objects = await objectsCachePromise;
				if (_isMounted.current) {
					setAllObjects(objects);
					setObjectsLoaded(true);
				}
				return;
			}

			objectsCachePromise = objectService.getImplementedObjects();
			const objects = await objectsCachePromise;

			objectsCache = objects;

			if (_isMounted.current) {
				setAllObjects(objects);
				setObjectsLoaded(true);
			}
		} catch (error) {
			console.error('Error loading objects:', error);
			objectsCachePromise = null;

			if (_isMounted.current) {
				setObjectsLoaded(true);
			}
		}
	};

	const resetInputText = (newValue) => {
		setInputText(newValue ?? '');
		setResults([]);
	};

	const handleTextChange = (e) => {
		const inputText = e.target.value;
		resetInputText(inputText);
		const searchText = inputText.trim();
		props.onChange(null);

		if (searchTimeout.current) {
			clearTimeout(searchTimeout.current);
		}

		if (searchText.length <= 2) {
			return;
		}

		setLoading(true);
		searchTimeout.current = setTimeout(() => {
			performSearch(searchText);
		}, 300);
	};

	const performSearch = (searchText: string) => {
		if (!_isMounted.current || !objectsLoaded) {
			setLoading(false);
			return;
		}

		try {
			const filteredResults = allObjects.filter((obj) =>
				obj.name?.toLowerCase().includes(searchText.toLowerCase())
			);

			if (_isMounted.current) {
				console.log('aca', filteredResults);
				setResults(filteredResults.slice(0, 50));
				setLoading(false);
			}
		} catch (error) {
			console.error('Error filtering objects:', error);
			if (_isMounted.current) {
				setResults([]);
				setLoading(false);
			}
		}
	};

	const renderedResults =
		results.length !== 0 || inputText?.length < 3 || loading ? (
			results.map((obj) => (
				<TypeaheadItem value={obj.id.toString()} key={obj.id}>
					<React.Fragment>
						<strong>{obj.name}</strong>
					</React.Fragment>
				</TypeaheadItem>
			))
		) : (
			<TypeaheadItem value={'0'} aria-disabled={true}>
				<React.Fragment>
					<i>No objects found.</i>
				</React.Fragment>
			</TypeaheadItem>
		);

	const isLoading = loading || !objectsLoaded;

	return !props.disabled ? (
		<>
			<Typeahead
				clickHandler={(id) => {
					console.log(id);
					const obj = results.find((o) => o.id === parseInt(id));
					console.log(obj);
					if (obj) {
						setInputText('');
						setResults([]);
						props.onChange(obj);
					}
				}}
				label={props.label}
				optionalClass={!isValid ? ' em-has-error' : ''}
				note={nonValidMessage || (!props.noteLink ? props.note : null)}
				aria-disabled={props.disabled}
				onChange={handleTextChange}
				placeholder={props.placeholder}
				value={inputText}
			>
				{isLoading ? <LoadingIndicator size="s" /> : renderedResults}
			</Typeahead>
			{props.noteLink ? (
				<TextPassage>
					<p className="em-c-field__note" style={!isValid ? { color: '#ca6a72' } : null}>
						{props.note}
						<TextLink href={props.noteLink} external>
							More info...
						</TextLink>
					</p>
				</TextPassage>
			) : null}
		</>
	) : (
		<ReadOnlyField label={props.label} text={inputText} />
	);
};

export default ObjectLookup;
